import { X } from 'lucide-react';
import { getPatientPhysiciansOptions, type OrderViewModel, type PatientPhysicianViewModel } from '@generated';
import { FormModal } from '~/shared/forms/FormModal';
import { useSuspenseQuery } from '@tanstack/react-query';
import { SelectField } from '~/shared/forms/controls/base-fields/SelectField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { Button } from '~/shared/components/buttons/Button';
import { PhysicianRowLabel } from './PhysicianRowLabel';
import { getInvalidateOrderQueryKeys } from '../util';
import type { FormMutationProps } from '~/shared/forms/types';

interface PhysicianRow {
	patientPhysicianId: string;
	notes: string | null;
}

export function OrderPhysiciansFormModal({
	title,
	order,
	currentPhysicians,
	filterEligible,
	saveMutation,
	commandListKey,
	closeModal
}: {
	title: string;
	order: OrderViewModel;
	currentPhysicians: { patientPhysicianId: string; patientPhysician: PatientPhysicianViewModel; notes?: string }[];
	filterEligible: (patientPhysician: PatientPhysicianViewModel) => boolean;
	saveMutation: FormMutationProps;
	commandListKey: 'newSigningPatientPhysicians' | 'copiedPatientPhysicians';
	closeModal: () => void;
}) {
	const { data: patientPhysicians } = useSuspenseQuery(getPatientPhysiciansOptions(order.stayId));

	const eligible = patientPhysicians.physicians.filter(filterEligible);

	const findPatientPhysician = (patientPhysicianId: string) =>
		patientPhysicians.physicians.find(p => p.id === patientPhysicianId) ??
		currentPhysicians.find(p => p.patientPhysicianId === patientPhysicianId)?.patientPhysician;

	return (
		<FormModal
			title={title}
			initialValues={{
				orderId: order.id,
				physicians: currentPhysicians.map(p => ({ patientPhysicianId: p.patientPhysicianId, notes: p.notes ?? null }))
			}}
			saveMutation={{
				...saveMutation,
				invalidateQueryKeys: getInvalidateOrderQueryKeys(order)
			}}
			onSaveTransform={values => ({
				orderId: values.orderId,
				[commandListKey]: (values.physicians as PhysicianRow[]).map(({ patientPhysicianId, notes }) => ({ patientPhysicianId, notes }))
			})}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			{formHelpers => {
				const rows = formHelpers.values.physicians as PhysicianRow[];
				const available = eligible.filter(p => !rows.some(r => r.patientPhysicianId === p.id));
				return (
					<div className="flex flex-col gap-6">
						<SelectField
							label="Add Physician"
							placeholder={available.length === 0 ? 'No physicians available' : 'Select a physician...'}
							isDisabled={available.length === 0}
							items={available.map(p => ({ id: p.id, name: p.physician.fullName }))}
							value={null}
							onChange={id =>
								id && formHelpers.setFieldValue('physicians', [...rows, { patientPhysicianId: id.toString(), notes: null }])
							}
						/>
						<div className="flex flex-col gap-2">
							{rows.length === 0 ? (
								<p className="text-sm text-gray-500">No physicians added yet.</p>
							) : (
								rows.map((row, index) => {
									const patientPhysician = findPatientPhysician(row.patientPhysicianId);
									return (
										<div
											key={row.patientPhysicianId}
											className="flex items-center gap-4 rounded border border-gray-200 px-3 py-2"
										>
											<div className="flex-1">
												{patientPhysician ? (
													<PhysicianRowLabel patientPhysician={patientPhysician} />
												) : (
													<p className="text-sm text-gray-500">Unknown physician</p>
												)}
											</div>
											<div className="flex-1">
												<TextAreaFormField
													name={`physicians[${index}].notes`}
													label="Notes"
													hideLabel
													inputProps={{ rows: 2, placeholder: 'Notes (optional)' }}
												/>
											</div>
											<Button
												Icon={X}
												appearance="outline"
												intent="destructive"
												aria-label={`Remove ${patientPhysician?.physician.fullName ?? 'physician'}`}
												onClick={() =>
													formHelpers.setFieldValue(
														'physicians',
														rows.filter(r => r.patientPhysicianId !== row.patientPhysicianId)
													)
												}
											/>
										</div>
									);
								})
							)}
						</div>
					</div>
				);
			}}
		</FormModal>
	);
}
